import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { useLoungeChat } from './hooks/useLoungeChat';
import { useShowLoungeBanner } from './hooks/useShowLoungeBanner';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { openNewTab } from '../../utils/UIUtils';
import { formatDateTime } from '../../utils/DateUtil';
import ChatUI from '../components/chat/ChatUI';
import LoungeMessageInput from './components/LoungeMessageInput';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import NewPostLoadButton from './components/NewPostLoadButton';
import { ArrowLeft } from '@peacecover/icon';
import LoungeBanner from './components/LoungeBanner';

const LoungeChatPage = () => {
  const { colorMode } = useColorMode();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const categoryGroup = searchParams.get('categoryGroup');
  const forReward = searchParams.get('forReward') === 'true';

  const [isShowNewPostButton, setIsShowNewPostButton] = useState<boolean>(false);
  const {
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    posts,
    myProfileId,
    profileImageUrl,
    latestPostsCount,
    handleLikeLoungeMessage,
    handleFollowUser
  } = useLoungeChat(categoryGroup || 'community');

  const { isShowBanner, setIsShowBanner, isWishLounge } = useShowLoungeBanner();

  useEffect(() => {
    if (latestPostsCount && latestPostsCount.latestPostsCount > 0) {
      setIsShowNewPostButton(true);
    } else {
      setIsShowNewPostButton(false);
    }
  }, [latestPostsCount]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasScrolledInitially = useRef(false);
  const hasAnonymousBubble = useRef(true);

  const handleHistoryBack = useHandleHistoryBack();

  // scroll 이벤트를 처리하기 위해 post 데이터에 chatRoomMessageId 추가
  const scrollPosts = useMemo(
    () => (posts ?? []).map((p) => ({ ...p, chatRoomMessageId: p.postId || '' })),
    [posts]
  );

  useInfiniteScroll({
    fetchNextPage,
    isFetching,
    hasNextPage: hasNextPage ?? false,
    data: scrollPosts
  });

  const saveScrollPosition = useCallback(() => {
    const y = window.scrollY || window.pageYOffset || 0;
    sessionStorage.setItem('loungeScrollY', String(y));
  }, []);

  const restoreScrollPosition = useCallback(() => {
    const y = Number(sessionStorage.getItem('loungeScrollY') || '0');
    // iOS/Safari 대응: 레이아웃 안정화 후 복원
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    });
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        saveScrollPosition();
      } else if (document.visibilityState === 'visible') {
        restoreScrollPosition();
      }
    };
    document.addEventListener('visibilitychange', onVis, { passive: true });
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) restoreScrollPosition();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [saveScrollPosition, restoreScrollPosition]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    if (posts.length > 0 && !hasScrolledInitially.current) {
      // DOM 렌더링이 완료된 후 스크롤을 보장하기 위해 setTimeout을 사용
      setTimeout(() => {
        scrollToBottom('auto');
        hasScrolledInitially.current = true;
      }, 0);
    }
  }, [posts.length, scrollToBottom]);
  // end of scroll event handling

  const handleLoadMoreClick = useCallback(() => {
    queryClient.invalidateQueries(['Posts']);
    scrollToBottom('smooth');
    setIsShowNewPostButton(false);
  }, [queryClient, scrollToBottom]);

  // 하단 도달 시 자동으로 새로 로드하고 버튼 숨김
  useEffect(() => {
    const onScroll = () => {
      if (!isShowNewPostButton) return;
      const doc = document.documentElement;
      const distanceFromBottom = doc.scrollHeight - (doc.scrollTop + window.innerHeight);
      if (distanceFromBottom <= 150) {
        handleLoadMoreClick();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isShowNewPostButton, handleLoadMoreClick]);

  const handleNavigateDetailPage = (postId: string) => {
    if (postId && !postId.includes('temp')) {
      saveScrollPosition();
      openNewTab(`/posts/${postId}`);
    }
  };

  const handleNavigateProfilePage = (userProfileId: string) => {
    if (userProfileId.trim()) {
      openNewTab(`/feed/user_profile/${userProfileId}`);
    }
  };

  if (!posts.length && isFetching) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>
          {categoryGroup === 'nctwish'
            ? t(`LoungeChatPage.${categoryGroup}`)
            : t('LoungeChatPage.title')}
        </title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <div className="fixed top-0 left-0 right-0 z-10">
        <header className="relative flex items-center justify-center px-[16px] pb-[10px] pt-[calc(10px+env(safe-area-inset-top))] bg-pure-white dark:bg-background-dark">
          <button type="button" className="absolute left-[16px]" onClick={handleHistoryBack}>
            <ArrowLeft size={24} className="text-text-main-lv1" />
          </button>
          <h1 className="text-[17px] font-bold text-text-main-lv1 leading-[20px]">
            {categoryGroup ? t(`LoungeChatPage.${categoryGroup}`) : t('LoungeChatPage.title')}
          </h1>
        </header>
      </div>
      <div className="mt-[calc(env(safe-area-inset-top)+60px)]">
        {isFetchingNextPage && (
          <div className="flex items-center justify-center gap-[6px] mt-[12px] mb-[16px]">
            <GradientLoadingSpinner size={20} />
            <p className="text-[11px] text-text-main-lv2 font-medium">
              {t('BettieChatRoom.loadingPrevious')}
            </p>
          </div>
        )}
        {isShowBanner && (
          <div className="fixed top-[calc(env(safe-area-inset-top)+50px)] w-full z-[100] left-0 right-0">
            <LoungeBanner onClose={() => setIsShowBanner(false)} isWishLounge={isWishLounge} />
          </div>
        )}
        <div
          className="flex flex-col gap-[38px] pl-[12px] pr-[8px] pb-[calc(110px+env(safe-area-inset-bottom))]"
          data-lounge-list-container
        >
          {posts.map((post, idx) => {
            const currentDay = new Date((post.authoredTimestamp ?? 0) * 1000).toDateString();
            const prevDay =
              idx > 0
                ? new Date((posts[idx - 1].authoredTimestamp ?? 0) * 1000).toDateString()
                : '';
            const shouldShowDateHeader = idx === 0 || currentDay !== prevDay;

            return (
              <Fragment key={post.postId}>
                {shouldShowDateHeader && (
                  <DateHeader date={formatDateTime((post.authoredTimestamp ?? 0) * 1000, 'PPP')} />
                )}
                <div id={`message-${post.postId}`}>
                  <ChatUI
                    post={post}
                    onBubbleClick={() => handleNavigateDetailPage(post.postId ?? '')}
                    onFollowUser={() =>
                      handleFollowUser(myProfileId ?? '', post.userProfileId ?? '')
                    }
                    onLike={() => handleLikeLoungeMessage(post.postId ?? '')}
                    onProfileClick={() => {
                      handleNavigateProfilePage(post.userProfileId ?? '');
                    }}
                    onComment={() => handleNavigateDetailPage(post.postId ?? '')}
                  />
                </div>
              </Fragment>
            );
          })}
        </div>
        <div ref={messagesEndRef} />
      </div>
      <div className="fixed bottom-0 w-full left-0 right-0">
        {isShowNewPostButton && <NewPostLoadButton onClick={handleLoadMoreClick} />}
        <LoungeMessageInput
          profileImageUrl={profileImageUrl}
          hasAnonymousBubble={hasAnonymousBubble}
          categoryGroup={categoryGroup || 'FreeTalk'}
          onActiveChange={(active) => {
            const container = document.querySelector(
              '[data-lounge-list-container]'
            ) as HTMLDivElement | null;
            if (container) {
              container.style.paddingBottom = `calc(${active ? 250 : 110}px + env(safe-area-inset-bottom))`;
            }
          }}
          isForReward={forReward}
        />
      </div>
    </div>
  );
};

export default LoungeChatPage;

const DateHeader = ({ date }: { date: string }) => {
  return (
    <div className="flex justify-center -my-[15px]">
      <div className="py-[8px]">
        <span className="text-[12px] font-medium text-text-main-lv4">{date}</span>
      </div>
    </div>
  );
};

